import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  MapPinIcon,
  UserIcon,
  ClockIcon,
  BoltIcon,
  ChartBarIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import { format, formatDistanceToNow } from 'date-fns';
import axiosInstance from '../../utils/axios';
import DeviceIcon from '../../components/DeviceIcon';
import DeviceSubscriptions from '../../components/DeviceSubscriptions';
import DeviceHistory from '../../components/DeviceHistory';
import AlarmHistory from '../AlarmHistory';

// One granted device, read-only: the three live views (through the vendor
// WebSocket, which only accepts subscriptions), stored history, and alarm
// history. No terminal, no beta2, no custom data, no writes anywhere.
const VendorDeviceDetail = () => {
  const { serial } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('live');

  useEffect(() => {
    const fetchDevice = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/vendor/devices/${serial}`);
        setDevice(response.data.device);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch device:', err);
        setError(
          err.response?.status === 404
            ? 'This device is not shared with your company.'
            : 'Failed to load the device. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    };
    fetchDevice();
  }, [serial]);

  const tabs = [
    { id: 'live', label: 'Live Views', icon: BoltIcon },
    { id: 'history', label: 'History', icon: ChartBarIcon },
    { id: 'alarms', label: 'Alarm History', icon: BellAlertIcon },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/vendor')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back to devices
        </button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  const owner = device.owners?.[0];
  const ownerLabel = owner
    ? `${owner.farmName ? owner.farmName + ' — ' : ''}${`${owner.firstName || ''} ${owner.lastName || ''}`.trim()}`
    : null;

  return (
    <div className="space-y-6">
      {/* Back + header */}
      <button
        onClick={() => navigate('/vendor')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        Back to devices
      </button>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-50 rounded-xl">
              <DeviceIcon model={device.model} className="w-8 h-8 text-primary-700" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 font-mono">
                {device.serial}
              </h1>
            </div>
          </div>
          <span
            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium self-start md:self-auto ${
              device.isOnline ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                device.isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
              }`}
            ></span>
            {device.isOnline ? 'Online' : 'Offline'}
          </span>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-400 mb-0.5">Dryer</p>
            <p className="text-gray-900 font-medium">
              {[device.dryerManufacturer?.name, device.dryerModel].filter(Boolean).join(' ') || 'Not on file'}
            </p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5 flex items-center gap-1">
              <UserIcon className="w-3.5 h-3.5" /> Owner
            </p>
            <p className="text-gray-900 font-medium">{ownerLabel || 'Unclaimed'}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5 flex items-center gap-1">
              <MapPinIcon className="w-3.5 h-3.5" /> Location
            </p>
            <p className="text-gray-900 font-medium">{device.location?.formatted || 'Unknown'}</p>
          </div>
          <div>
            <p className="text-gray-400 mb-0.5 flex items-center gap-1">
              <ClockIcon className="w-3.5 h-3.5" /> {device.isOnline ? 'Registered' : 'Last seen'}
            </p>
            <p className="text-gray-900 font-medium">
              {device.isOnline
                ? device.registeredAt
                  ? format(new Date(device.registeredAt), 'MMM d, yyyy')
                  : 'Unknown'
                : device.lastSessionOffline
                  ? formatDistanceToNow(new Date(device.lastSessionOffline), { addSuffix: true })
                  : 'Unknown'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-3 border-b-2 font-medium text-sm transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {activeTab === 'live' && (
        <DeviceSubscriptions
          deviceSerial={device.serial}
          isOnline={device.isOnline}
          model={device.model}
          readOnly
        />
      )}

      {activeTab === 'history' && (
        <DeviceHistory deviceId={device.serial} vendor />
      )}

      {activeTab === 'alarms' && (
        <AlarmHistory deviceSerial={device.serial} apiBase="/vendor" canExport={false} />
      )}
    </div>
  );
};

export default VendorDeviceDetail;
